import {useSharedFilter} from '../../../shared/navigation/useSharedReport';
// src/features/budgetVsSpent/components/BudgetVsSpent.jsx

import React, { useEffect, useMemo, useState } from "react";
import { getDoc, doc } from "firebase/firestore";

import { db } from "../../../firebase.js";
import { buildRevenueColumns } from "../../../budget/model/columns/columnBuilders.js";
import { sumSectionTotals } from "../../../budget/utils/programBudgetUtils.js";
import {ReportPage} from "../../../shared/ui/layout/ReportPage";
import TownSpendingOverview from "./TownSpendingOverview";
import townBanner from "../images/town-budget-vs-spent-banner.png";
import boeBanner from "../images/boe-budget-vs-spent-banner.png";
import { fetchExpenditureSections } from "../../townBudget/data/fetchExpenditureSections.js";
import {
  DEBT_SECTION_ORDER,
  TOWN_SECTION_ORDER,
} from "../../../shared/constants/tableOrder.js";
import { PROGRAM_CONFIG } from "../../../shared/constants/programBudget.js";
import { LineChartWithToggles } from "../../../shared/ui/chart/LineChartWithToggles.jsx";
import { HeaderToggleButton } from "../../../shared/ui/overlay/HeaderToggleButton.jsx";
import { TableSearchScope, SectionTableSearch } from "../../../shared/ui/table/TableSearchScope";
import { CommonTable } from "../../../shared/ui/table/CommonTable.jsx";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";
import {
  buildAverageVarianceRows,
  buildBoeVarianceRows,
  buildTownVarianceRows,
  getComparableBoeYears,
  getComparableTownYears,
} from "../utils/budgetVsSpentUtils.js";

function CollapsibleSection({ title, children, defaultCollapsed = false }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
      <HeaderToggleButton
        reportNavigation
        title={title}
        controlsId={`budget-report-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        isOpen={!collapsed}
        onToggle={() => setCollapsed(value => !value)}
      />

      {!collapsed && <div id={`budget-report-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="bg-white p-4"><TableSearchScope>{children}</TableSearchScope></div>}
    </div>
  );
}

function ReportToolbar({ children }) {
  const [description, ...controls] = React.Children.toArray(children);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-gray-100 px-3 py-2">
      <div className="min-w-0 flex-1">{description}</div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
        <SectionTableSearch />
        {controls}
      </div>
    </div>
  );
}

function formatTownFiscalYear(endYear) {
  return Number.isFinite(endYear) ? `${endYear - 1}-${endYear}` : "";
}

function YearSelect({ label, value, options, onChange, formatLabel }) {
  if (!options.length) return null;

  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="font-semibold text-gray-700">{label}</span>
      <select
        className="border rounded px-2 py-1 text-xs bg-white"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {options.map((year) => (
          <option key={year} value={year}>
            {formatLabel(year)}
          </option>
        ))}
      </select>
    </div>
  );
}

function buildVarianceColumns(primaryLabel) {
  return [
    {
      key: "label",
      label: primaryLabel,
      style: { width: "340px", minWidth: "340px" },
    },
    {
      key: "group",
      label: "Group",
      style: { width: "220px", minWidth: "220px" },
    },
    {
      key: "approved",
      label: "Approved Budget",
      format: "currency",
      align: "right",
      style: { width: "150px", minWidth: "150px" },
    },
    {
      key: "actual",
      label: "Spent",
      format: "currency",
      align: "right",
      style: { width: "150px", minWidth: "150px" },
    },
    {
      key: "variance",
      label: "Variance",
      format: "currency",
      align: "right",
      style: { width: "140px", minWidth: "140px" },
    },
    {
      key: "variancePct",
      label: "Variance %",
      format: "percent",
      align: "right",
      style: { width: "120px", minWidth: "120px" },
    },
  ];
}

function buildAverageVarianceColumns(primaryLabel) {
  return [
    {
      key: "label",
      label: primaryLabel,
      style: { width: "340px", minWidth: "340px" },
    },
    {
      key: "group",
      label: "Group",
      style: { width: "220px", minWidth: "220px" },
    },
    {
      key: "approved",
      label: "Avg Budget",
      format: "currency",
      align: "right",
      style: { width: "160px", minWidth: "160px" },
    },
    {
      key: "actual",
      label: "Avg Spent",
      format: "currency",
      align: "right",
      style: { width: "160px", minWidth: "160px" },
    },
    {
      key: "variance",
      label: "Avg Variance",
      format: "currency",
      align: "right",
      style: { width: "150px", minWidth: "150px" },
    },
    {
      key: "variancePct",
      label: "Avg Variance %",
      format: "percent",
      align: "right",
      style: { width: "130px", minWidth: "130px" },
    },
  ];
}

function buildTownDetailedAverageColumns() {
  return [
    {
      key: "label",
      label: "Area",
      style: { width: "340px", minWidth: "340px" },
    },
    {
      key: "approved",
      label: "Avg Budget",
      format: "currency",
      align: "right",
      style: { width: "160px", minWidth: "160px" },
    },
    {
      key: "actual",
      label: "Avg Spent",
      format: "currency",
      align: "right",
      style: { width: "160px", minWidth: "160px" },
    },
    {
      key: "variance",
      label: "Avg Variance",
      format: "currency",
      align: "right",
      style: { width: "150px", minWidth: "150px" },
    },
    {
      key: "variancePct",
      label: "Avg Variance %",
      format: "percent",
      align: "right",
      style: { width: "130px", minWidth: "130px" },
    },
  ];
}

function buildTownDetailedAverageDisplayData(groupedSectionsByYear, years) {
  const targetYears = Array.isArray(years) ? years.filter(Number.isFinite) : [];
  if (!targetYears.length) return [];

  const sectionsMap = new Map();

  targetYears.forEach((year) => {
    const grouped = groupedSectionsByYear?.[year] || {};

    Object.values(grouped).forEach((sections) => {
      (sections || []).forEach((section) => {
        const sectionName = section?.name || "Other";
        const sectionBucket = sectionsMap.get(sectionName) || new Map();

        (section.rows || []).forEach((row) => {
          const label = row?.label;
          if (!label) return;

          const existing =
            sectionBucket.get(label) || {
              label,
              approvedSum: 0,
              actualSum: 0,
              varianceSum: 0,
              variancePctSum: 0,
              variancePctCount: 0,
              yearsIncluded: 0,
            };

          const approved = row?.y2_adopted;
          const actual = row?.y2_actual;

          if (
            typeof approved === "number" &&
            Number.isFinite(approved) &&
            typeof actual === "number" &&
            Number.isFinite(actual)
          ) {
            const variance = actual - approved;
            existing.approvedSum += approved;
            existing.actualSum += actual;
            existing.varianceSum += variance;
            if (approved !== 0) {
              existing.variancePctSum += variance / approved;
              existing.variancePctCount += 1;
            }
            existing.yearsIncluded += 1;
          }

          sectionBucket.set(label, existing);
        });

        sectionsMap.set(sectionName, sectionBucket);
      });
    });
  });

  const displayRows = [];

  Array.from(sectionsMap.entries()).forEach(([sectionName, rowsMap]) => {
    displayRows.push({ section: sectionName });

    const sectionRows = Array.from(rowsMap.values())
      .filter((row) => row.yearsIncluded > 0)
      .map((row) => {
        const avgVariance = row.varianceSum / row.yearsIncluded;
        const avgApproved = row.approvedSum / row.yearsIncluded;
        const avgActual = row.actualSum / row.yearsIncluded;

        return {
          label: row.label,
          approvedRaw: avgApproved,
          actualRaw: avgActual,
          varianceRaw: avgVariance,
          approved: roundAverageCurrency(avgApproved),
          actual: roundAverageCurrency(avgActual),
          variance: roundAverageCurrency(avgVariance),
          variancePct:
            row.variancePctCount > 0
              ? row.variancePctSum / row.variancePctCount
              : null,
        };
      });

    sectionRows.forEach((row) => displayRows.push(row));

    if (sectionRows.length > 1) {
      const totalApproved = sectionRows.reduce(
        (sum, row) => sum + (Number.isFinite(row.approvedRaw) ? row.approvedRaw : 0),
        0,
      );
      const totalActual = sectionRows.reduce(
        (sum, row) => sum + (Number.isFinite(row.actualRaw) ? row.actualRaw : 0),
        0,
      );
      const totalVariance = totalActual - totalApproved;

      displayRows.push({
        label: "Total",
        approved: roundAverageCurrency(totalApproved),
        actual: roundAverageCurrency(totalActual),
        variance: roundAverageCurrency(totalVariance),
        variancePct: totalApproved !== 0 ? totalVariance / totalApproved : null,
        rowType: "subtotal",
        bold: true,
      });
    }
  });

  return displayRows;
}

function roundAverageCurrency(value) {
  return Number.isFinite(value) ? Math.round(value) : value;
}

function normalizeBoeDetailBlockName(blockName) {
  const raw = String(blockName || "").trim();

  if (
    /^(program\s*\/\s*staff\s*development|program development|staff development)$/i.test(
      raw,
    )
  ) {
    return "Program / Staff Development";
  }

  return raw;
}

function normalizeBoeDetailLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;

  if (/^teacher\s*\(reading\)$/i.test(raw)) return "Teachers (Reading)";
  if (/^teachers?\s*\(reading\)$/i.test(raw)) return "Teachers (Reading)";
  if (/^dues\s+and\s+fees$/i.test(raw)) return "Dues / Fees";
  if (/^dues\s*&\s*fees$/i.test(raw)) return "Dues / Fees";
  if (/^dues\/fees$/i.test(raw)) return "Dues / Fees";
  if (/^dues\s*\/\s*fees$/i.test(raw)) return "Dues / Fees";

  if (/^technology\s*[-/]?\s*software(\s*\(.+\))?$/i.test(raw)) {
    const suffix = raw.match(/(\s*\(.+\))$/)?.[1] || "";
    return `Technology / Software${suffix}`;
  }

  if (/^in[\s-]*class\s+tutors\s*\/?\s*paras$/i.test(raw)) {
    return "In Class Tutors / Paras";
  }

  if (/^lang\.?\s*arts\s*\/\s*math\s*-?\s*tutors\s*\/?\s*paras$/i.test(raw)) {
    return "Lang Arts / Math Tutors / Paras";
  }

  if (/^drivers?\s*\/?\s*early literacy program(?:\s*\(na\))?$/i.test(raw)) {
    return "Drivers / Early Literacy Program";
  }
  if (/^driver early literacy program$/i.test(raw)) {
    return "Drivers / Early Literacy Program";
  }

  if (/^exploring the arts(?:\s*\(gw\))?$/i.test(raw)) return "Exploring the Arts";
  if (/^magnet school$/i.test(raw)) return "Magnet Schools";
  if (/^summer school$/i.test(raw)) return "Summer School (AE)";

  if (/^paraprofessional,\s*p-t,\s*science$/i.test(raw)) {
    return "Paraprofessionals, P-T, Science";
  }

  return raw;
}

function normalizeBoeDetailKey(value) {
  return normalizeBoeDetailLabel(value)
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[&/.,-]+/g, " ")
    .replace(/\bdrivers\b/g, "driver")
    .replace(/\bparaprofessionals\b/g, "paraprofessional")
    .replace(/\bmagnet schools\b/g, "magnet school")
    .replace(/\bdues and fees\b/g, "dues fees")
    .replace(/\s+/g, " ")
    .trim();
}

function isBoeDetailValueBlock(blockName, blockValue) {
  return (
    typeof blockValue === "object" &&
    blockValue != null &&
    !Array.isArray(blockValue) &&
    !/^positions$/i.test(String(blockName || "")) &&
    !/^total(?: expenditures)?$/i.test(String(blockName || ""))
  );
}

function buildBoeDetailedAverageDisplayData(programDocs, programConfig, years) {
  const targetYears = Array.isArray(years) ? years.filter(Number.isFinite) : [];
  if (!targetYears.length) return [];

  const displayRows = [];

  (programConfig || []).forEach((cfg) => {
    const buckets = new Map();
    let sectionApprovedTotalSum = 0;
    let sectionActualTotalSum = 0;

    targetYears.forEach((year) => {
      const yearNode = programDocs?.[cfg.docId]?.[String(year)];
      const approvedSection = yearNode?.Approved;
      const actualSection = yearNode?.Actual;
      const approvedSectionTotal = sumSectionTotals(approvedSection);
      const actualSectionTotal = sumSectionTotals(actualSection);

      if (Number.isFinite(approvedSectionTotal)) {
        sectionApprovedTotalSum += approvedSectionTotal;
      }
      if (Number.isFinite(actualSectionTotal)) {
        sectionActualTotalSum += actualSectionTotal;
      }

      const blockNames = new Set([
        ...Object.entries(approvedSection || {})
          .filter(([blockName, blockValue]) =>
            isBoeDetailValueBlock(blockName, blockValue),
          )
          .map(([blockName]) => normalizeBoeDetailBlockName(blockName)),
        ...Object.entries(actualSection || {})
          .filter(([blockName, blockValue]) =>
            isBoeDetailValueBlock(blockName, blockValue),
          )
          .map(([blockName]) => normalizeBoeDetailBlockName(blockName)),
      ]);

      blockNames.forEach((blockName) => {
        const approvedBlocks = Object.entries(approvedSection || {})
          .filter(([sourceBlockName, blockValue]) =>
            isBoeDetailValueBlock(sourceBlockName, blockValue),
          )
          .filter(
            ([sourceBlockName]) =>
              normalizeBoeDetailBlockName(sourceBlockName) === blockName,
          );
        const actualBlocks = Object.entries(actualSection || {})
          .filter(([sourceBlockName, blockValue]) =>
            isBoeDetailValueBlock(sourceBlockName, blockValue),
          )
          .filter(
            ([sourceBlockName]) =>
              normalizeBoeDetailBlockName(sourceBlockName) === blockName,
          );
        const normalizedItems = new Map();

        approvedBlocks.forEach(([, approvedBlock]) => {
          Object.entries(approvedBlock || {}).forEach(([itemKey, value]) => {
            if (/^total$/i.test(String(itemKey))) return;
            const normalizedKey = normalizeBoeDetailKey(itemKey);
            if (!normalizedKey) return;

            const slot = normalizedItems.get(normalizedKey) || {
              displayLabel: normalizeBoeDetailLabel(itemKey),
              approved: 0,
              actual: 0,
            };

            if (typeof value === "number" && Number.isFinite(value)) {
              slot.approved += value;
            }
            normalizedItems.set(normalizedKey, slot);
          });
        });

        actualBlocks.forEach(([, actualBlock]) => {
          Object.entries(actualBlock || {}).forEach(([itemKey, value]) => {
            if (/^total$/i.test(String(itemKey))) return;
            const normalizedKey = normalizeBoeDetailKey(itemKey);
            if (!normalizedKey) return;

            const slot = normalizedItems.get(normalizedKey) || {
              displayLabel: normalizeBoeDetailLabel(itemKey),
              approved: 0,
              actual: 0,
            };

            slot.displayLabel = normalizeBoeDetailLabel(itemKey);

            if (typeof value === "number" && Number.isFinite(value)) {
              slot.actual += value;
            }
            normalizedItems.set(normalizedKey, slot);
          });
        });

        normalizedItems.forEach(({ displayLabel, approved, actual }, itemKey) => {
          const rowKey = `${blockName}::${itemKey}`;
          const existing =
            buckets.get(rowKey) || {
              label: `${blockName} - ${displayLabel}`,
              approvedSum: 0,
              actualSum: 0,
              varianceSum: 0,
            };

          const variance = actual - approved;
          existing.approvedSum += approved;
          existing.actualSum += actual;
          existing.varianceSum += variance;

          buckets.set(rowKey, existing);
        });
      });
    });

    const rows = Array.from(buckets.values())
      .filter(
        (row) =>
          row.approvedSum !== 0 || row.actualSum !== 0 || row.varianceSum !== 0,
      )
      .map((row) => {
        const avgApproved = row.approvedSum / targetYears.length;
        const avgActual = row.actualSum / targetYears.length;
        const avgVariance = row.varianceSum / targetYears.length;

        return {
          label: row.label,
          approvedRaw: avgApproved,
          actualRaw: avgActual,
          varianceRaw: avgVariance,
          approved: roundAverageCurrency(avgApproved),
          actual: roundAverageCurrency(avgActual),
          variance: roundAverageCurrency(avgVariance),
          varianceAbs: Math.abs(avgVariance),
          variancePct: avgApproved !== 0 ? avgVariance / avgApproved : null,
        };
      })
      .sort((a, b) => b.varianceAbs - a.varianceAbs || a.label.localeCompare(b.label));

    if (!rows.length) return;

    const totalApproved = sectionApprovedTotalSum / targetYears.length;
    const totalActual = sectionActualTotalSum / targetYears.length;
    const totalVariance = totalActual - totalApproved;

    displayRows.push({ section: cfg.label });
    rows.forEach((row) => displayRows.push(row));
    displayRows.push({
      label: "Total",
      approved: roundAverageCurrency(totalApproved),
      actual: roundAverageCurrency(totalActual),
      variance: roundAverageCurrency(totalVariance),
      variancePct: totalApproved !== 0 ? totalVariance / totalApproved : null,
      rowType: "subtotal",
      bold: true,
    });
  });

  return displayRows;
}

function buildMetricChartByYear(
  rowsByYear,
  years,
  metricKey,
  valueType = "currency",
  formatYearLabel = String,
  defaultVisibleCount = 0,
  totalSeriesLabel = null,
) {
  const safeYears = Array.isArray(years) ? years.filter(Number.isFinite) : [];
  if (!safeYears.length) return { labels: [], series: [] };
  const isPercentMetric = valueType === "percent";

  const seriesMap = new Map();
  const magnitudeMap = new Map();

  safeYears.forEach((year) => {
    const rows = Array.isArray(rowsByYear?.[year]) ? rowsByYear[year] : [];

    rows.forEach((row) => {
      const key = `${row.group}:${row.label}`;
      const existing =
        seriesMap.get(key) || {
          key,
          label: row.label,
          data: Array(safeYears.length).fill(null),
          valueType,
          group:
            typeof row.group === "string" && row.group.trim()
              ? row.group
              : "other",
          defaultVisible: false,
        };

      const idx = safeYears.indexOf(year);
      if (idx >= 0) {
        const value = row?.[metricKey];
        existing.data[idx] =
          typeof value === "number" && Number.isFinite(value) ? value : null;
        if (existing.data[idx] != null && isPercentMetric) {
          existing.data[idx] = existing.data[idx] * 100;
        }

        if (typeof value === "number" && Number.isFinite(value)) {
          magnitudeMap.set(
            key,
            (magnitudeMap.get(key) || 0) + Math.abs(value),
          );
        }
      }

      seriesMap.set(key, existing);
    });
  });

  const series = Array.from(seriesMap.values())
    .sort((a, b) => {
      const diff = (magnitudeMap.get(b.key) || 0) - (magnitudeMap.get(a.key) || 0);
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label);
    })
    .map((item, idx) => ({
      ...item,
      defaultVisible: idx < defaultVisibleCount,
    }));

  if (metricKey === "variancePct" && totalSeriesLabel) {
    const totalData = safeYears.map((year) => {
      const rows = Array.isArray(rowsByYear?.[year]) ? rowsByYear[year] : [];

      let approvedTotal = 0;
      let actualTotal = 0;
      let sawAny = false;

      rows.forEach((row) => {
        if (
          typeof row?.approved === "number" &&
          Number.isFinite(row.approved) &&
          typeof row?.actual === "number" &&
          Number.isFinite(row.actual)
        ) {
          approvedTotal += row.approved;
          actualTotal += row.actual;
          sawAny = true;
        }
      });

      if (!sawAny || approvedTotal === 0) return null;
      return ((actualTotal - approvedTotal) / approvedTotal) * 100;
    });

    series.unshift({
      key: "__total__",
      label: totalSeriesLabel,
      data: totalData,
      valueType: "percent",
      group: "other",
      color: "#16a34a",
      defaultVisible: false,
      _hideFromToggles: true,
    });
  }

  return {
    labels: safeYears.map((year) => formatYearLabel(year)),
    series,
  };
}

const TOWN_DOC_IDS = {
  town: "expenditures_town",
  debtServiceAndTransfers: "expenditures_debt_transfers_out",
  education: "expenditures_education",
};

const townColumns = buildVarianceColumns("Area");
const boeColumns = buildVarianceColumns("Program").filter(
  (col) => col.key !== "group",
);
const townAverageColumns = buildAverageVarianceColumns("Area");
const townDetailedAverageColumns = buildTownDetailedAverageColumns();
const boeAverageColumns = buildAverageVarianceColumns("Program").filter(
  (col) => col.key !== "group",
);
const boeDetailedAverageColumns = buildTownDetailedAverageColumns();
const townDetailedSectionOrder = [
  ...TOWN_SECTION_ORDER,
  ...DEBT_SECTION_ORDER,
  "Education",
];

export function BudgetVsSpent({context = 'town'}) {
  const [loading, setLoading] = useState(true);

  const [townSectionsYear, setTownSectionsYear] = useState(null);
  const [townYears, setTownYears] = useState([]);
  const [townYear, setTownYear] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:townYear", null);
  const [townRawDocs, setTownRawDocs] = useState({});
  const [townSections, setTownSections] = useState({
    town: [],
    debtServiceAndTransfers: [],
    education: [],
  });

  const [boeYears, setBoeYears] = useState([]);
  const [boeYear, setBoeYear] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:boeYear", null);
  const [boeProgramDocs, setBoeProgramDocs] = useState({});
  const [townAverageWindow, setTownAverageWindow] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:townAverageWindow", 5);
  const [townDetailedAverageWindow, setTownDetailedAverageWindow] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:townDetailedAverageWindow", 5);
  const [boeAverageWindow, setBoeAverageWindow] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:boeAverageWindow", 5);
  const [boeDetailedAverageWindow, setBoeDetailedAverageWindow] = useSharedFilter("BudgetVsSpent.jsx:BudgetVsSpent:boeDetailedAverageWindow", 5);
  const [showTownAverageBudgetChart, setShowTownAverageBudgetChart] =
    useState(false);
  const [showTownAverageSpentChart, setShowTownAverageSpentChart] =
    useState(false);
  const [showTownAverageVarianceChart, setShowTownAverageVarianceChart] =
    useState(false);
  const [showTownAverageVariancePctChart, setShowTownAverageVariancePctChart] =
    useState(false);
  const [showBoeAverageBudgetChart, setShowBoeAverageBudgetChart] =
    useState(false);
  const [showBoeAverageSpentChart, setShowBoeAverageSpentChart] =
    useState(false);
  const [showBoeAverageVarianceChart, setShowBoeAverageVarianceChart] =
    useState(false);
  const [showBoeAverageVariancePctChart, setShowBoeAverageVariancePctChart] =
    useState(false);
  const [townDetailedSectionsByYear, setTownDetailedSectionsByYear] = useState({});

  const boeConfigWithDocIds = useMemo(
    () =>
      PROGRAM_CONFIG.map((cfg) => {
        const match = cfg.key.match(/^(\d{4})/);
        return { ...cfg, docId: match ? match[1] : cfg.key };
      }),
    [],
  );

  useEffect(() => {
    let alive = true;

    async function loadInitialData() {
      setLoading(true);

      try {
        if (context === 'town') {
        const townEntries = Object.entries(TOWN_DOC_IDS);
        const townSnaps = await Promise.all(
          townEntries.map(([, docId]) => getDoc(doc(db, "townGovernment", docId))),
        );

        if (!alive) return;

        const townDocs = townEntries.reduce((acc, [groupKey], idx) => {
          acc[groupKey] = townSnaps[idx].exists() ? townSnaps[idx].data() : {};
          return acc;
        }, {});

        setTownRawDocs(townDocs);
        const nextTownYears = getComparableTownYears(townDocs);
        setTownYears(nextTownYears);
        setTownYear.initialize(nextTownYears[nextTownYears.length - 1] || null);
        } else {
        const boeSnaps = await Promise.all(
          boeConfigWithDocIds.map((cfg) =>
            getDoc(doc(db, "publicSchools", cfg.docId)),
          ),
        );

        if (!alive) return;

        const nextBoeDocs = boeConfigWithDocIds.reduce((acc, cfg, idx) => {
          acc[cfg.docId] = boeSnaps[idx].exists() ? boeSnaps[idx].data() : {};
          return acc;
        }, {});

        const nextBoeYears = getComparableBoeYears(nextBoeDocs);
        setBoeProgramDocs(nextBoeDocs);
        setBoeYears(nextBoeYears);
        setBoeYear.initialize(nextBoeYears[nextBoeYears.length - 1] || null);
        }
      } catch (err) {
        console.error("[BudgetVsSpent] Failed to load comparison data:", err);
        if (!alive) return;

        setTownYears([]);
        setTownYear.initialize(null);
        setTownRawDocs({});
        setBoeProgramDocs({});
        setBoeYears([]);
        setBoeYear.initialize(null);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadInitialData();
    return () => {
      alive = false;
    };
  }, [boeConfigWithDocIds, context]);

  useEffect(() => {
    if (!Number.isFinite(townYear)) return;

    let alive = true;

    async function loadTownSections() {
      try {
        const baseColumns = buildRevenueColumns(townYear + 2).baseColumns;
        const [town, debtServiceAndTransfers, education] = await Promise.all([
          fetchExpenditureSections("town", baseColumns, townRawDocs.town),
          fetchExpenditureSections(
            "debtServiceAndTransfers",
            baseColumns,
            townRawDocs.debtServiceAndTransfers,
          ),
          fetchExpenditureSections("education", baseColumns, townRawDocs.education),
        ]);

        if (!alive) return;

        setTownSectionsYear(townYear);
        setTownSections({
          town: town.sections || [],
          debtServiceAndTransfers: debtServiceAndTransfers.sections || [],
          education: education.sections || [],
        });
      } catch (err) {
        console.error("[BudgetVsSpent] Failed to load town sections:", err);
        if (!alive) return;
        setTownSectionsYear(townYear);
        setTownSections({
          town: [],
          debtServiceAndTransfers: [],
          education: [],
        });
      }
    }

    loadTownSections();
    return () => {
      alive = false;
    };
  }, [townYear, townRawDocs]);

  const townRows = useMemo(
    () => buildTownVarianceRows({ comparisonYear: townYear, groupedSections: townSections }),
    [townYear, townSections],
  );

  const boeRows = useMemo(
    () =>
      buildBoeVarianceRows({
        comparisonYear: boeYear,
        programDocs: boeProgramDocs,
        programConfig: boeConfigWithDocIds,
      }),
    [boeYear, boeProgramDocs, boeConfigWithDocIds],
  );

  const averageWindowOptions = useMemo(() => {
    const maxYears = Math.max(townYears.length, boeYears.length, 5);
    return Array.from({ length: maxYears }, (_, idx) => idx + 1);
  }, [townYears.length, boeYears.length]);

  const townAverageYears = useMemo(
    () => townYears.slice(-Math.max(1, townAverageWindow)),
    [townYears, townAverageWindow],
  );
  const townDetailedAverageYears = useMemo(
    () => townYears.slice(-Math.max(1, townDetailedAverageWindow)),
    [townYears, townDetailedAverageWindow],
  );
  const boeAverageYears = useMemo(
    () => boeYears.slice(-Math.max(1, boeAverageWindow)),
    [boeYears, boeAverageWindow],
  );
  const boeDetailedAverageYears = useMemo(
    () => boeYears.slice(-Math.max(1, boeDetailedAverageWindow)),
    [boeYears, boeDetailedAverageWindow],
  );

  const [townFiveYearData, setTownFiveYearData] = useState({});

  useEffect(() => {
    let alive = true;

    async function loadTownFiveYearData() {
      if (!townAverageYears.length) {
        setTownFiveYearData({});
        setTownDetailedSectionsByYear({});
        return;
      }

      const entries = await Promise.all(
        townAverageYears.map(async (year) => {
          const baseColumns = buildRevenueColumns(year + 2).baseColumns;
          const [town, debtServiceAndTransfers, education] = await Promise.all([
            fetchExpenditureSections("town", baseColumns, townRawDocs.town),
            fetchExpenditureSections(
              "debtServiceAndTransfers",
              baseColumns,
              townRawDocs.debtServiceAndTransfers,
            ),
            fetchExpenditureSections("education", baseColumns, townRawDocs.education),
          ]);

          return [
            year,
            buildTownVarianceRows({
              comparisonYear: year,
              groupedSections: {
                town: town.sections || [],
                debtServiceAndTransfers: debtServiceAndTransfers.sections || [],
                education: education.sections || [],
              },
            }),
          ];
        }),
      );

      if (!alive) return;
      setTownFiveYearData(Object.fromEntries(entries));
    }

    loadTownFiveYearData().catch((err) => {
      console.error("[BudgetVsSpent] Failed to load five-year Town data:", err);
      if (alive) setTownFiveYearData({});
    });

    return () => {
      alive = false;
    };
  }, [townAverageYears, townRawDocs]);

  const townAverageRows = useMemo(
    () => buildAverageVarianceRows(townFiveYearData, townAverageYears),
    [townFiveYearData, townAverageYears],
  );

  useEffect(() => {
    let alive = true;

    async function loadTownDetailedAverageData() {
      if (!townDetailedAverageYears.length) {
        setTownDetailedSectionsByYear({});
        return;
      }

      const entries = await Promise.all(
        townDetailedAverageYears.map(async (year) => {
          const baseColumns = buildRevenueColumns(year + 2).baseColumns;
          const [town, debtServiceAndTransfers, education] = await Promise.all([
            fetchExpenditureSections("town", baseColumns, townRawDocs.town),
            fetchExpenditureSections(
              "debtServiceAndTransfers",
              baseColumns,
              townRawDocs.debtServiceAndTransfers,
            ),
            fetchExpenditureSections("education", baseColumns, townRawDocs.education),
          ]);

          return [
            year,
            {
              town: town.sections || [],
              debtServiceAndTransfers: debtServiceAndTransfers.sections || [],
              education: education.sections || [],
            },
          ];
        }),
      );

      if (!alive) return;
      setTownDetailedSectionsByYear(Object.fromEntries(entries));
    }

    loadTownDetailedAverageData().catch((err) => {
      console.error("[BudgetVsSpent] Failed to load detailed Town average data:", err);
      if (alive) setTownDetailedSectionsByYear({});
    });

    return () => {
      alive = false;
    };
  }, [townDetailedAverageYears, townRawDocs]);

  const townDetailedAverageRows = useMemo(
    () =>
      buildTownDetailedAverageDisplayData(
        townDetailedSectionsByYear,
        townDetailedAverageYears,
      ),
    [townDetailedSectionsByYear, townDetailedAverageYears],
  );

  const townAverageBudgetChart = useMemo(
    () =>
      buildMetricChartByYear(
        townFiveYearData,
        townAverageYears,
        "approved",
        "currency",
        formatTownFiscalYear,
      ),
    [townFiveYearData, townAverageYears],
  );
  const townAverageSpentChart = useMemo(
    () =>
      buildMetricChartByYear(
        townFiveYearData,
        townAverageYears,
        "actual",
        "currency",
        formatTownFiscalYear,
      ),
    [townFiveYearData, townAverageYears],
  );
  const townAverageVarianceChart = useMemo(
    () =>
      buildMetricChartByYear(
        townFiveYearData,
        townAverageYears,
        "variance",
        "currency",
        formatTownFiscalYear,
      ),
    [townFiveYearData, townAverageYears],
  );
  const townAverageVariancePctChart = useMemo(
    () =>
      buildMetricChartByYear(
        townFiveYearData,
        townAverageYears,
        "variancePct",
        "percent",
        formatTownFiscalYear,
        5,
        "Total Average Variance Percent",
      ),
    [townFiveYearData, townAverageYears],
  );

  const boeAverageDataByYear = useMemo(
    () =>
      Object.fromEntries(
        boeAverageYears.map((year) => [
          year,
          buildBoeVarianceRows({
            comparisonYear: year,
            programDocs: boeProgramDocs,
            programConfig: boeConfigWithDocIds,
          }),
        ]),
      ),
    [boeAverageYears, boeProgramDocs, boeConfigWithDocIds],
  );

  const boeAverageRows = useMemo(
    () => buildAverageVarianceRows(boeAverageDataByYear, boeAverageYears),
    [boeAverageDataByYear, boeAverageYears],
  );
  const boeDetailedAverageRows = useMemo(
    () =>
      buildBoeDetailedAverageDisplayData(
        boeProgramDocs,
        boeConfigWithDocIds,
        boeDetailedAverageYears,
      ),
    [boeProgramDocs, boeConfigWithDocIds, boeDetailedAverageYears],
  );
  const boeDetailedSectionOrder = useMemo(
    () => boeConfigWithDocIds.map((cfg) => cfg.label),
    [boeConfigWithDocIds],
  );

  const boeAverageBudgetChart = useMemo(
    () =>
      buildMetricChartByYear(
        boeAverageDataByYear,
        boeAverageYears,
        "approved",
        "currency",
        formatFiscalYearFromStart,
      ),
    [boeAverageDataByYear, boeAverageYears],
  );
  const boeAverageSpentChart = useMemo(
    () =>
      buildMetricChartByYear(
        boeAverageDataByYear,
        boeAverageYears,
        "actual",
        "currency",
        formatFiscalYearFromStart,
      ),
    [boeAverageDataByYear, boeAverageYears],
  );
  const boeAverageVarianceChart = useMemo(
    () =>
      buildMetricChartByYear(
        boeAverageDataByYear,
        boeAverageYears,
        "variance",
        "currency",
        formatFiscalYearFromStart,
      ),
    [boeAverageDataByYear, boeAverageYears],
  );
  const boeAverageVariancePctChart = useMemo(
    () =>
      buildMetricChartByYear(
        boeAverageDataByYear,
        boeAverageYears,
        "variancePct",
        "percent",
        formatFiscalYearFromStart,
        5,
        "Total Average Variance Percent",
      ),
    [boeAverageDataByYear, boeAverageYears],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200 text-base-content flex items-center justify-center">
        <div className="text-sm text-gray-600">Loading budget vs spent report…</div>
      </div>
    );
  }

  return (
    <ReportPage
      editorial
      title={context === 'town' ? 'Town Budget vs Spent' : 'Board of Education — Budget vs Spent'}
      banner={context === 'town' ? townBanner : boeBanner}
      bannerAlt={context === 'town' ? 'Watercolor illustration of the Barnes & Noble storefront in Glastonbury' : 'Watercolor illustration of the arched shopping center entrance and Talbots storefronts in Glastonbury'}
      description={context === 'town' ? 'Compare what Glastonbury approved in its town budget with what it actually spent. Explore differences by expenditure area, compare completed fiscal years, and look at average spending patterns over time. A positive variance means spending exceeded the approved budget; a negative variance means spending was below budget.' : 'Compare the Board of Education’s approved budgets with actual spending. Explore differences by program, compare completed fiscal years, and examine average spending patterns down to individual line items. A positive variance means spending exceeded the approved budget; a negative variance means spending was below budget.'}
    >
            <div className="space-y-6">
              {context === 'town' ? <TownSpendingOverview rows={townRows} year={townYear} years={townYears} onYearChange={setTownYear} ready={townSectionsYear === townYear} /> : <TownSpendingOverview context="boe" rows={boeRows} year={boeYear} years={boeYears} onYearChange={setBoeYear} ready={!loading} />}
              {context === 'town' && <><CollapsibleSection
                title="TOWN BUDGET OUTLIERS"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Largest completed-year variances across Town expenditure areas.
                    </p>
                    <YearSelect
                      label="Fiscal Year"
                      value={townYear}
                      options={townYears}
                      onChange={setTownYear}
                      formatLabel={formatTownFiscalYear}
                    />
                  </ReportToolbar>

                  {townRows.length > 0 ? (
                    <CommonTable
                      title={null}
                      columns={townColumns}
                      data={townRows}
                      showAggregateTotal={false}
                      filterEmptyRows={false}
                      defaultSortField="variance"
                      defaultSortOrder="desc"
                      headerColor="bg-[#d5e7f7]"
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      No Town comparison rows are available for the selected year.
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                title="TOWN BUDGET AVERAGE OUTLIERS"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Average annual variance across comparable Town fiscal years.
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-gray-700">
                        Average over Years
                      </span>
                      <select
                        className="border rounded px-2 py-1 text-xs bg-white"
                        value={townAverageWindow}
                        onChange={(e) => setTownAverageWindow(Number(e.target.value))}
                      >
                        {averageWindowOptions.map((years) => (
                          <option key={years} value={years}>
                            {years}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ReportToolbar>

                  {townAverageRows.length > 0 ? (
                    <>
                      <CommonTable
                        title={null}
                        columns={townAverageColumns}
                        data={townAverageRows}
                        showAggregateTotal={false}
                        filterEmptyRows={false}
                        defaultSortField="variance"
                        defaultSortOrder="desc"
                        headerColor="bg-[#d5e7f7]"
                      />

                      <div className="space-y-6 pt-2">
                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showTownAverageBudgetChart}
                            onToggle={() =>
                              setShowTownAverageBudgetChart((prev) => !prev)
                            }
                            title="Average Budget"
                            ariaLabel="Toggle town average budget chart"
                            controlsId="town-average-budget-chart"
                          />

                          {showTownAverageBudgetChart && (
                            <div id="town-average-budget-chart">
                              <LineChartWithToggles
                                labels={townAverageBudgetChart.labels}
                                series={townAverageBudgetChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Budget"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showTownAverageSpentChart}
                            onToggle={() =>
                              setShowTownAverageSpentChart((prev) => !prev)
                            }
                            title="Average Spent"
                            ariaLabel="Toggle town average spent chart"
                            controlsId="town-average-spent-chart"
                          />

                          {showTownAverageSpentChart && (
                            <div id="town-average-spent-chart">
                              <LineChartWithToggles
                                labels={townAverageSpentChart.labels}
                                series={townAverageSpentChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Spent"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showTownAverageVarianceChart}
                            onToggle={() =>
                              setShowTownAverageVarianceChart((prev) => !prev)
                            }
                            title="Average Variance"
                            ariaLabel="Toggle town average variance chart"
                            controlsId="town-average-variance-chart"
                          />

                          {showTownAverageVarianceChart && (
                            <div id="town-average-variance-chart">
                              <LineChartWithToggles
                                labels={townAverageVarianceChart.labels}
                                series={townAverageVarianceChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Variance"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showTownAverageVariancePctChart}
                            onToggle={() =>
                              setShowTownAverageVariancePctChart((prev) => !prev)
                            }
                            title="Average Variance Percent"
                            ariaLabel="Toggle town average variance percent chart"
                            controlsId="town-average-variance-pct-chart"
                          />

                          {showTownAverageVariancePctChart && (
                            <div id="town-average-variance-pct-chart">
                              <LineChartWithToggles
                                labels={townAverageVariancePctChart.labels}
                                series={townAverageVariancePctChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Variance Percent"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">
                      No five-year Town comparison rows are available.
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                title="TOWN BUDGET AVERAGE OUTLIERS : DETAILED"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Average annual variance across comparable Town fiscal years at the subsection level.
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-gray-700">
                        Average over Years
                      </span>
                      <select
                        className="border rounded px-2 py-1 text-xs bg-white"
                        value={townDetailedAverageWindow}
                        onChange={(e) =>
                          setTownDetailedAverageWindow(Number(e.target.value))
                        }
                      >
                        {averageWindowOptions.map((years) => (
                          <option key={years} value={years}>
                            {years}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ReportToolbar>

                  {townDetailedAverageRows.length > 0 ? (
                    <CommonTable
                      title={null}
                      columns={townDetailedAverageColumns}
                      data={townDetailedAverageRows}
                      showAggregateTotal={false}
                      filterEmptyRows={false}
                      defaultSortField="variance"
                      defaultSortOrder="desc"
                      headerColor="bg-[#d5e7f7]"
                      sectionOrder={townDetailedSectionOrder}
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      No detailed Town comparison rows are available.
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              </>}
              {context === 'boe' && <><CollapsibleSection
                title="BOARD OF EDUCATION OUTLIERS"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Largest completed-year variances across Board of Education programs.
                    </p>
                    <YearSelect
                      label="Fiscal Year"
                      value={boeYear}
                      options={boeYears}
                      onChange={setBoeYear}
                      formatLabel={formatFiscalYearFromStart}
                    />
                  </ReportToolbar>

                  {boeRows.length > 0 ? (
                    <CommonTable
                      title={null}
                      columns={boeColumns}
                      data={boeRows}
                      showAggregateTotal={false}
                      filterEmptyRows={false}
                      defaultSortField="variance"
                      defaultSortOrder="desc"
                      headerColor="bg-[#d5e7f7]"
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      No Board of Education comparison rows are available for the selected year.
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                title="BOARD OF EDUCATION AVERAGE OUTLIERS"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Average annual variance across comparable Board of Education fiscal years.
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-gray-700">
                        Average over Years
                      </span>
                      <select
                        className="border rounded px-2 py-1 text-xs bg-white"
                        value={boeAverageWindow}
                        onChange={(e) => setBoeAverageWindow(Number(e.target.value))}
                      >
                        {averageWindowOptions.map((years) => (
                          <option key={years} value={years}>
                            {years}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ReportToolbar>

                  {boeAverageRows.length > 0 ? (
                    <>
                      <CommonTable
                        title={null}
                        columns={boeAverageColumns}
                        data={boeAverageRows}
                        showAggregateTotal={false}
                        filterEmptyRows={false}
                        defaultSortField="variance"
                        defaultSortOrder="desc"
                        headerColor="bg-[#d5e7f7]"
                      />

                      <div className="space-y-6 pt-2">
                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showBoeAverageBudgetChart}
                            onToggle={() =>
                              setShowBoeAverageBudgetChart((prev) => !prev)
                            }
                            title="Average Budget"
                            ariaLabel="Toggle boe average budget chart"
                            controlsId="boe-average-budget-chart"
                          />

                          {showBoeAverageBudgetChart && (
                            <div id="boe-average-budget-chart">
                              <LineChartWithToggles
                                labels={boeAverageBudgetChart.labels}
                                series={boeAverageBudgetChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Budget"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showBoeAverageSpentChart}
                            onToggle={() =>
                              setShowBoeAverageSpentChart((prev) => !prev)
                            }
                            title="Average Spent"
                            ariaLabel="Toggle boe average spent chart"
                            controlsId="boe-average-spent-chart"
                          />

                          {showBoeAverageSpentChart && (
                            <div id="boe-average-spent-chart">
                              <LineChartWithToggles
                                labels={boeAverageSpentChart.labels}
                                series={boeAverageSpentChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Spent"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showBoeAverageVarianceChart}
                            onToggle={() =>
                              setShowBoeAverageVarianceChart((prev) => !prev)
                            }
                            title="Average Variance"
                            ariaLabel="Toggle boe average variance chart"
                            controlsId="boe-average-variance-chart"
                          />

                          {showBoeAverageVarianceChart && (
                            <div id="boe-average-variance-chart">
                              <LineChartWithToggles
                                labels={boeAverageVarianceChart.labels}
                                series={boeAverageVarianceChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Variance"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>

                        <div className="space-y-1">
                          <HeaderToggleButton
                            isOpen={showBoeAverageVariancePctChart}
                            onToggle={() =>
                              setShowBoeAverageVariancePctChart((prev) => !prev)
                            }
                            title="Average Variance Percent"
                            ariaLabel="Toggle boe average variance percent chart"
                            controlsId="boe-average-variance-pct-chart"
                          />

                          {showBoeAverageVariancePctChart && (
                            <div id="boe-average-variance-pct-chart">
                              <LineChartWithToggles
                                labels={boeAverageVariancePctChart.labels}
                                series={boeAverageVariancePctChart.series}
                                showTotalWhenNoneSelected={true}
                                showSeriesToggles={true}
                                totalLabel="Average Variance Percent"
                                showChartTypeSelector={true}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">
                      No five-year Board of Education comparison rows are available.
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              <CollapsibleSection
                title="BOARD OF EDUCATION AVERAGE OUTLIERS : DETAILED"
                defaultCollapsed={true}
              >
                <div className="space-y-3">
                  <ReportToolbar>
                    <p className="text-sm text-gray-600">
                      Average annual variance across comparable Board of Education fiscal years at the line-item level.
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-gray-700">
                        Average over Years
                      </span>
                      <select
                        className="border rounded px-2 py-1 text-xs bg-white"
                        value={boeDetailedAverageWindow}
                        onChange={(e) =>
                          setBoeDetailedAverageWindow(Number(e.target.value))
                        }
                      >
                        {averageWindowOptions.map((years) => (
                          <option key={years} value={years}>
                            {years}
                          </option>
                        ))}
                      </select>
                    </div>
                  </ReportToolbar>

                  <p className="text-xs text-gray-500">
                    Detailed rows are line items within each program, so they will
                    not match the program-level totals shown above.
                  </p>

                  {boeDetailedAverageRows.length > 0 ? (
                    <CommonTable
                      title={null}
                      columns={boeDetailedAverageColumns}
                      data={boeDetailedAverageRows}
                      showAggregateTotal={false}
                      filterEmptyRows={false}
                      defaultSortField="variance"
                      defaultSortOrder="desc"
                      headerColor="bg-[#d5e7f7]"
                      sectionOrder={boeDetailedSectionOrder}
                    />
                  ) : (
                    <div className="text-sm text-gray-500">
                      No detailed Board of Education comparison rows are available.
                    </div>
                  )}
                </div>
              </CollapsibleSection>
              </>}
            </div>
    </ReportPage>
  );
}
